/**
 * Em qual fatura o CDR de um bloco pode ser aplicado (modal Processar CDR).
 *
 * Até 05/10/2026 o select "Aplicar CDR na fatura" listava as faturas em Rascunho da
 * base INTEIRA, de qualquer cliente: o bloco do SRPF-GO oferecia a fatura do CFQ. E a
 * pré-seleção automática escolhia a primeira fatura Rascunho OU Aberta do cliente, que
 * o select não conseguia mostrar (ele só listava Rascunho): o lote aplicava numa fatura
 * invisível. FAT202610043 (UFMA, Aberta) ficou R$ 6,66 acima da NFCom 1025 já autorizada.
 *
 * Agora: só faturas do cliente do bloco, menos as Canceladas, cada uma dizendo o estado
 * ANTES do clique (status, nota autorizada, pagamento). Não esconde as que têm nota: às
 * vezes aplicar é o que se quer (sem tarifa de saída o CDR é só detalhamento). Mostra e
 * rotula; quem decide é o operador. A pré-seleção automática só escolhe Rascunho.
 */

export interface ClienteDoCdr {
  id: number;
  name?: string | null;
  tradeName?: string | null;
  companyName?: string | null;
  cdrTag?: string | null;
}

export interface FaturaDoCdr {
  id: number;
  code: string;
  clientId: number;
  referenceMonth: string;
  status: string;
  paidValue?: number | null;
  /** Fatura consolidada: a nota pode ter sido emitida para o GRUPO, não para a linha. */
  invoiceGroupId?: number | null;
}

export interface NotaDoCdr {
  invoiceId: number | null;
  invoiceGroupId?: number | null;
  type: string;
  status: string;
  documentNumber?: string | null;
}

export interface OpcaoDeFatura {
  id: number;
  code: string;
  referenceMonth: string;
  status: string;
  /** "NFCom 1025", "NFS-e 88": só as autorizadas. */
  notasAutorizadas: string[];
  pago: number;
  /** A fatura já saiu para o mundo: aplicar CDR pode deixá-la acima da nota. */
  temNotaAutorizada: boolean;
  /**
   * Não se sabe se há nota: as notas não carregaram, ou a fatura é consolidada e a nota do
   * grupo não está visível. NÃO SEI não é "sem nota": "sem nota" é o sinal verde do rótulo,
   * e afirmá-lo sem saber é a tela abençoando o clique. Trata-se como risco.
   */
  notaNaoVerificada: boolean;
  rotulo: string;
}

const norm = (s: unknown) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();

/**
 * O cliente de um bloco do CDR. A mesma regra que a pré-seleção sempre usou, extraída
 * para o select e a pré-seleção não discordarem: 1) `cdrTag` explícito, 2) nome,
 * 3) nome fantasia, 4) a etiqueta como PALAVRA inteira do nome, com um único candidato,
 * 5) todas as palavras da etiqueta no nome, com um único candidato.
 */
export function clienteDoBlocoCdr<C extends ClienteDoCdr>(clientTag: string, clients: C[]): C | null {
  const tag = norm(clientTag);
  if (!tag) return null;
  let c = clients.find((x) => x.cdrTag && norm(x.cdrTag) === tag)
    || clients.find((x) => norm(x.name) === tag)
    || clients.find((x) => norm(x.tradeName) === tag);
  if (c) return c;

  if (tag.length >= 4) {
    const esc = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const palavra = new RegExp(`(^|[^A-Z0-9])${esc}([^A-Z0-9]|$)`);
    const cand = clients.filter((x) => palavra.test(norm(x.name)) || palavra.test(norm(x.tradeName)));
    if (cand.length === 1) return cand[0];
  }

  const tokens = tag.split(/[^A-Z0-9]+/).filter((t) => t.length >= 3);
  if (tokens.length >= 2) {
    const temTodas = (s: unknown) => {
      const set = new Set(norm(s).split(/[^A-Z0-9]+/).filter(Boolean));
      return tokens.every((t) => set.has(t));
    };
    const cand = clients.filter((x) => temTodas(x.name) || temTodas(x.tradeName));
    if (cand.length === 1) return cand[0];
  }
  return null;
}

/** O que a tela diz quando a etiqueta não casa com cliente nenhum: problema de cadastro. */
export function semClienteParaEtiqueta(clientTag: string): string {
  return `A etiqueta "${clientTag}" deste CDR não está mapeada em nenhum cliente. Preencha o campo Etiqueta do CDR (cdrTag) no cadastro do cliente.`;
}

const nomeDaNota = (n: NotaDoCdr) => `${String(n.type).toLowerCase() === 'nfcom' ? 'NFCom' : 'NFS-e'} ${n.documentNumber || 's/nº'}`;

const juntar = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`);

/**
 * As faturas do cliente em que o CDR pode entrar, Rascunho primeiro e depois por
 * competência decrescente (o mais seguro em cima). `notas` = documentos fiscais do
 * cliente; `null` enquanto não chegaram (o rótulo então não afirma "sem nota").
 */
export function opcoesDeFaturaParaCdr(
  cliente: { id: number; nome: string } | null,
  invoices: FaturaDoCdr[],
  notas: NotaDoCdr[] | null,
  brl: (v: number) => string,
): OpcaoDeFatura[] {
  if (!cliente) return [];
  return invoices
    .filter((i) => i.clientId === cliente.id && i.status !== 'Cancelada')
    .sort((a, b) => {
      const ra = a.status === 'Rascunho' ? 0 : 1;
      const rb = b.status === 'Rascunho' ? 0 : 1;
      if (ra !== rb) return ra - rb;
      return String(b.referenceMonth || '').localeCompare(String(a.referenceMonth || '')) || b.id - a.id;
    })
    .map((i) => {
      const autorizadasDaLinha = notas ? notas.filter((n) => n.invoiceId === i.id && n.status === 'Autorizada').map(nomeDaNota) : [];
      const autorizadasDoGrupo = notas && i.invoiceGroupId
        ? notas.filter((n) => n.invoiceGroupId === i.invoiceGroupId && n.invoiceId !== i.id && n.status === 'Autorizada').map((n) => `${nomeDaNota(n)} (do grupo)`)
        : [];
      const autorizadas = [...autorizadasDaLinha, ...autorizadasDoGrupo];
      // A nota do grupo pode ter saído em nome de outro cliente do grupo, fora da consulta
      // por cliente: sem nota visível, consolidada fica "não verificada", nunca "sem nota".
      const naoVerificada = !autorizadas.length && (notas == null || !!i.invoiceGroupId);
      const pago = Number(i.paidValue) || 0;
      const estado = [i.status];
      if (autorizadas.length) estado.push(`${juntar(autorizadas)} ${autorizadas.length === 1 ? 'autorizada' : 'autorizadas'}`);
      else if (notas == null) estado.push('nota não verificada (não carregou)');
      else if (i.invoiceGroupId) estado.push('consolidada, nota do grupo não verificada');
      else estado.push('sem nota');
      if (pago > 0) estado.push(`R$ ${brl(pago)} pago`);
      return {
        id: i.id,
        code: i.code,
        referenceMonth: i.referenceMonth,
        status: i.status,
        notasAutorizadas: autorizadas,
        pago,
        temNotaAutorizada: autorizadas.length > 0,
        notaNaoVerificada: naoVerificada,
        rotulo: `${autorizadas.length || naoVerificada ? '⚠ ' : ''}${i.code} — ${cliente.nome} (${i.referenceMonth}) — ${estado.join(', ')}`,
      };
    });
}

/**
 * Pré-seleção automática: só quando há exatamente UM Rascunho do cliente. Fatura Aberta,
 * com nota ou paga, só por escolha explícita de quem está olhando o rótulo. Com dois
 * rascunhos (outubro e novembro), escolher um é adivinhar o mês, e a opção pré-selecionada
 * aparece na lista com cara de conferida: o campo abre em "Selecione a fatura...".
 */
export function faturaPreSelecionada(invoices: FaturaDoCdr[], clientId: number): number | null {
  const rascunhos = invoices.filter((i) => i.clientId === clientId && i.status === 'Rascunho');
  return rascunhos.length === 1 ? rascunhos[0].id : null;
}

/** Risco dito antes de aplicar: nota autorizada, nota não verificada ou pagamento. */
export function riscoDeAplicar(op: OpcaoDeFatura | null | undefined): string | null {
  if (!op || (!op.temNotaAutorizada && !op.notaNaoVerificada && op.pago <= 0)) return null;
  if (op.notaNaoVerificada && op.pago <= 0) {
    return `Não foi possível verificar se ${op.code} já tem nota autorizada. Se tiver e o CDR mudar o valor, a fatura fica diferente do que já saiu, e isso não se ajusta sozinho.`;
  }
  const tem: string[] = [];
  if (op.temNotaAutorizada) tem.push(`${juntar(op.notasAutorizadas)} ${op.notasAutorizadas.length === 1 ? 'autorizada' : 'autorizadas'}`);
  if (op.pago > 0) tem.push('pagamento registrado');
  return `${op.code} já tem ${juntar(tem)}. Se o CDR mudar o valor, a fatura fica diferente do que já saiu, e isso não se ajusta sozinho.`;
}

/**
 * O fim do lote, dito por inteiro: quanto aplicou e QUEM ficou de fora e por quê. Até
 * 05/10/2026 o bloco sem fatura selecionada era pulado sem uma palavra, e o lote
 * parecia ter feito tudo.
 */
export function resumoDoLote(r: { aplicados: number; erros: number; semFatura: string[]; semFlag: string[]; naoLidas?: string[]; motivoNaoLidas?: string | null }): { texto: string; tom: 'success' | 'warning' | 'error' } {
  const partes = [`CDR aplicado em ${r.aplicados}`];
  if (r.naoLidas?.length) {
    partes.push(`NÃO aplicado em ${r.naoLidas.length} porque não consegui ler o CDR que já está na fatura (${r.naoLidas.join(', ')}): ${r.motivoNaoLidas}. Nada foi gravado nesses blocos; aplique de novo`);
  }
  if (r.erros) partes.push(`${r.erros} com erro`);
  if (r.semFatura.length) partes.push(`pulado em ${r.semFatura.length} por falta de fatura selecionada (${r.semFatura.join(', ')})`);
  if (r.semFlag.length) partes.push(`pulado em ${r.semFlag.length} por flag de entrada desligada no contrato (${r.semFlag.join(', ')})`);
  const tom = r.erros || r.naoLidas?.length ? 'error' : r.semFatura.length || r.semFlag.length ? 'warning' : 'success';
  return { texto: `${partes.join('; ')}.`, tom };
}

/**
 * Os registros de CDR que JÁ estão na fatura, para preservar as outras direções ao aplicar uma (08/10/2026).
 *
 * Aplicar uma direção grava `cdrRecords = [...das outras direções, ...os novos]`. Se a leitura da fatura falhava e
 * virava `[]` (`.catch(() => null)`), as outras direções sumiam: aplicar a SAÍDA apagava o CDR de entrada e 0800 já
 * aplicado, consumo cobrado perdido sem aviso, logo abaixo do comentário que prometia preservá-las.
 *
 * `null` é legítimo: a coluna é jsonb nullable, e a fatura que nunca recebeu CDR vem com null. Chave AUSENTE não é:
 * sem ela não se sabe o que existe, e a aplicação recusa em vez de gravar por cima.
 */
export function registrosDoCdrJaAplicados(fatura: any): any[] {
  if (!fatura || !('cdrRecords' in fatura)) {
    throw new Error('a fatura veio sem os registros de CDR; sem eles, aplicar apagaria as outras direções');
  }
  return Array.isArray(fatura.cdrRecords) ? fatura.cdrRecords : [];
}
